import { Children, Fragment, type ReactElement, type ReactNode, isValidElement } from 'react';

import { MenuHeading } from './menu-heading';
import { MenuItem } from './menu-item';

/**
 * Splits menu children into a leading `Menu.Heading` (if present) and the
 * remaining nodes, and reports whether any of those remaining nodes are direct
 * `Menu.Item` elements.
 *
 * `Fragment` children are transparently flattened so that a consumer passing
 * `<><Menu.Item /><Menu.Item /></>` is treated the same as passing the items
 * as a sibling list. Callers can use `hasItems` to decide whether the
 * remainder should be wrapped in a `<ul>`. Only the first encountered
 * `Menu.Heading` is extracted; any later heading passes through in `rest`.
 */
export function extractMenuChildren(children: ReactNode): {
  heading: ReactElement | null;
  rest: Array<ReactNode>;
  hasItems: boolean;
} {
  let heading: ReactElement | null = null;
  const rest: Array<ReactNode> = [];
  let hasItems = false;

  const visit = (node: ReactNode) => {
    if (isValidElement(node) && node.type === Fragment) {
      Children.forEach((node.props as { children?: ReactNode }).children, visit);
      return;
    }
    if (heading === null && isValidElement(node) && node.type === MenuHeading) {
      heading = node as ReactElement;
      return;
    }
    if (isValidElement(node) && node.type === MenuItem) {
      hasItems = true;
    }
    rest.push(node);
  };

  Children.forEach(children, visit);

  return { heading, rest, hasItems };
}
